import React, { useState, useEffect, useContext, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Image, Modal, Spinner, OverlayTrigger, Tooltip, Dropdown, DropdownButton, ButtonGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePrompt } from '../hooks/usePrompt';
import { UserInfo, VisualCache } from '../helper/Context';
import VisualPickerModal from '../components/VisualPickerModal';
import CreateVisualFlow from '../components/CreateVisualFlow';
import { 
  loadChannelConfig, 
  addVisualToChannel 
} from '../helper/ChannelConfig';
import {
  APIAddEditPresentation,
  APICreateStoryFromPDF,
  APIFileVisualization,
  APIWebVisualization,
  APIAssetPromote,
} from "../helper/APIFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faImages,
  faPlus,
  faArrowUp,
  faArrowDown,
  faCheck,
  faShuffle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { isMobile } from '../helper/Utils';
import './ScrollytellingBuilderPage.css';
import { shallowCopy } from '../helper/Utils';
import ShareModal from '../components/VisualModals/Share';
import AiSparkleIcon from '../assets/AI-sparkle-icon.png';
import AiDraftModal from '../components/AiDraftModal';
import ReportInaccuraciesButton from '../components/ReportInaccuraciesButton';
import AccessCodeModal from '../components/AccessCodeModal';
import ScrollytellingInteractiveMatchModal from '../components/ScrollytellingInteractiveMatchModal';

const defaultMetadata = {
  title: '',
  author: '',
  authorUrl: '',
  abstract: '',
  date: new Date().toISOString().slice(0, 10),
};

const convertBackEndPresentationtoScrollytellingObject = (presentation, visCache) => {
  const story = {
    id: presentation.id,
    title: presentation.title,
    description: presentation.description || '',
    thumbnail: presentation.thumbnail || '',
    is_presentation: true,
    metadata: presentation.metadata,
    type: 'scrollytelling',
    url: presentation.url,
    qr_url: presentation.qr_url,
  };

  // Convert slides to sections
  story.slides = (presentation.slides || []).map((slide, index) => {
    const visual = visCache ? visCache[slide.visual_id] : null;
    const baseSection = {
      id: Date.now() + index, // Add a unique ID for UI operations
      type: slide.type || 'text', // Default to 'text' for backward compatibility
      visual_id: slide.visual_id,
      title: slide.header,
      visual: visual,
      visualUrl: visual ? visual.visualization_url : '',
    };
    
    if (slide.type === 'quiz') {
      // Quiz section
      return {
        ...baseSection,
        question: slide.question || '',
        answers: slide.answers || [
          { id: `ans-${Date.now()}-0`, text: '' },
          { id: `ans-${Date.now()}-1`, text: '' }
        ],
        correctAnswerIndex: slide.correctAnswerIndex || 0,
        text: '', // Empty text for quiz sections
      };
    } else {
      // Text section (default)
      return {
        ...baseSection,
        text: slide.intro,
        // Initialize quiz fields as empty for potential type switching
        question: '',
        answers: [
          { id: `ans-${Date.now()}-0`, text: '' },
          { id: `ans-${Date.now()}-1`, text: '' }
        ],
        correctAnswerIndex: 0,
      };
    }
  });

  return story;
};

// Ensure this is the correct entry point for your Vite dev server for the scrollytelling viewer
// const SCROLLYTELLING_VIEWER_DEV_BUNDLE_URL = 'http://localhost:5173/src/main.jsx';
// For a production build, you'd have a different URL:
// const SCROLLYTELLING_VIEWER_PROD_BUNDLE_URL = 'https://yourdomain.com/path/to/viewer/assets/index.somehash.js';

//TODO: Replace these with more stable .env variables
const SCROLLYTELLING_VIEWER_PROD_JS_URL = 'https://vis.snorkle.io/resources/scrollytelling/index-D_e6qxpg.js'; // Adjust if your build outputs a different name or path
const SCROLLYTELLING_VIEWER_PROD_CSS_URL = 'https://vis.snorkle.io/resources/scrollytelling/index-WfmfXeAm.css'; // Adjust if your build outputs a CSS file and you want to link it

const getGenerationStatusMessage = (status) => {
  switch (status) {
    case 'preparing':
      return 'Preparing...';
    case 'uploading':
      return 'Uploading...';
    case 'processing':
      return 'Processing...';
    default:
      return 'Generating...'; // Fallback message
  }
};

const getTextDirection = (text) => {
  if (!text) return 'ltr';
  // This regex checks for characters in the Arabic, Hebrew, and other RTL scripts.
  const rtlRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  return rtlRegex.test(text) ? 'rtl' : 'ltr';
};

const ScrollytellingBuilderPage = () => {
  const { userInfo } = useContext(UserInfo);
  const { visCache, setVisCache } = useContext(VisualCache);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we have draft content from content dashboard
  const draftSections = location.state?.draftSections;
  const draftMetadata = location.state?.draftMetadata;
  const addToChannelOnSave = location.state?.addToChannelOnSave;
  const channelEmail = location.state?.channelEmail;
  const openAiDraftFromPdf = location.state?.openAiDraftFromPdf;
  
  const [story, setStory] = useState(location.state?.presentation ? convertBackEndPresentationtoScrollytellingObject(location.state.presentation, visCache) : {});
  const [sections, setSections] = useState(draftSections || story.slides || []);
  const [saveState, setSaveState] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedState, setLastSavedState] = useState(null);
  const [pickerSectionIndex, setPickerSectionIndex] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showVisualPickerModal, setShowVisualPickerModal] = useState(false);
  const [modalView, setModalView] = useState('options');
  const [metadata, setMetadata] = useState(draftMetadata || defaultMetadata);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null); // 'preparing', 'uploading', 'processing'
  const [showAiDraftModal, setShowAiDraftModal] = useState(false);
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [candidateFigures, setCandidateFigures] = useState([]);
  const [isCreatingAiVisual, setIsCreatingAiVisual] = useState(false);
  const [initialSearchQuery, setInitialSearchQuery] = useState('');
  const [visualPickerDefaultView, setVisualPickerDefaultView] = useState('my-visuals');
  const pdfInputRef = useRef(null);
  const [showInteractiveMatchModal, setShowInteractiveMatchModal] = useState(false);
  const [interactiveMatchSectionIdx, setInteractiveMatchSectionIdx] = useState(null);

  const isOrg = false;

  useEffect(() => {
    if (location.state?.presentation) {
      const story = convertBackEndPresentationtoScrollytellingObject(location.state.presentation, visCache);
      setStory(story);
      setSections(story.slides || []);
      setMetadata(story.metadata || defaultMetadata);
      setHasUnsavedChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (openAiDraftFromPdf) {
      setShowAccessCodeModal(true);
    }
  }, [openAiDraftFromPdf]);

  usePrompt({
    when: hasUnsavedChanges,
    message: "You have unsaved changes. Do you want to leave without saving?",
  });

  useEffect(() => {
    if (isMobile()) {
      setShowMobileWarning(true);
    }
  }, []);

  // Logout while on this page — only redirect on explicit null (not undefined loading).
  // Do NOT toggle hasUnsavedChanges here: it fought the metadata/sections sync below and could
  // leave usePrompt permanently blocking in-app navigation after save or userInfo refresh.
  useEffect(() => {
    if (userInfo === null) {
      navigate('/home', { replace: true });
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    const currentState = { metadata, sections };

    if (!lastSavedState) {
      // If no saved state, consider it unsaved if there's any content
      const hasContent = metadata.title || metadata.author || metadata.abstract || sections.length > 0;
      setHasUnsavedChanges(hasContent);
      return;
    }

    const lastState = JSON.parse(lastSavedState);
    const hasChanges = JSON.stringify(currentState) !== JSON.stringify(lastState);
    setHasUnsavedChanges(hasChanges);
  }, [metadata, sections, lastSavedState]);

  useEffect(() => {
    // Enrich sections with full visual objects once visCache is populated.
    // Only depends on visCache — not sections — to avoid a render loop.
    if (!visCache || Object.keys(visCache).length === 0) return;

    setSections(currentSections => {
      const needsEnrichment = currentSections.some(s => s.visual_id && !s.visual);
      if (!needsEnrichment) return currentSections;

      return currentSections.map(section => {
        if (section.visual_id && !section.visual) {
          const visualFromCache = visCache[section.visual_id];
          if (visualFromCache) {
            return {
              ...section,
              visual: visualFromCache,
              visualUrl: visualFromCache.visualization_url,
            };
          }
        }
        return section;
      });
    });
  }, [visCache]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePdfUpload = (file, language) => {
    // This function now receives the file and language from the modal
    if (file && file.type === "application/pdf") {
      if (
        hasUnsavedChanges &&
        !window.confirm(
          "This will replace your current work. Are you sure?"
        )
      ) {
        return;
      }
      setGenerationStatus("preparing"); // Initial status
      setCandidateFigures([]); // Clear previous candidates
      APICreateStoryFromPDF(
        file,
        language,
        (storyData) => {
          try {
          } catch (e) {}
          // The storyData is the direct JSON object from the 'story_data' field in DynamoDB
          if (storyData && storyData.title && storyData.sections) {
            setMetadata({
              title: storyData.title || "",
              author: storyData.author || userInfo.name || "",
              authorUrl: storyData.authorUrl || "",
              abstract: storyData.summary || "",
              date: new Date().toISOString().slice(0, 10),
            });
            // Extract all visual URLs from the sections to create the list of candidate figures
            const figures = storyData.sections
              .map((section) => section.visualUrl || section.visual_url)
              .filter(Boolean);
            try {
            } catch (e) {}
            setCandidateFigures(figures);
            const newSections = storyData.sections.map((section, index) => ({
              id: Date.now() + index, // Assign a temporary frontend ID
              visual: null,
              title: section.title || '',
              text: section.text || '',
              visualSuggestion: section.visualSuggestion || section.visual_suggestion || '',
              // Backend may use camelCase or snake_case; both must map or UI shows no extracted figure.
              visualUrl: section.visualUrl || section.visual_url || '',
            }));
            try {
            } catch (e) {}
            setSections(newSections);
            setHasUnsavedChanges(true); // Mark as unsaved
            setLastSavedState(null);   // Clear last saved state
          } else {
            alert("Failed to generate story. The data received was incomplete.");
          }
          setGenerationStatus('done'); // Set status to 'done' instead of null
        },
        (error) => {
          alert(`An error occurred while generating the story: ${error.message || error}`);
          setGenerationStatus('done'); // Also set to 'done' on failure to show the modal can be closed
        },
        (status) => {
          setGenerationStatus(status); // Update status from progress callback
        }
      );
    }
  };

  const handleMoveSectionUp = (idx) => {
    if (idx === 0) return;
    setSections(currentSections => {
      const newSections = [...currentSections];
      [newSections[idx - 1], newSections[idx]] = [newSections[idx], newSections[idx - 1]];
      return newSections;
    });
  };

  const handleMoveSectionDown = (idx) => {
    if (idx === sections.length - 1) return;
    setSections(currentSections => {
      const newSections = [...currentSections];
      [newSections[idx], newSections[idx + 1]] = [newSections[idx + 1], newSections[idx]];
      return newSections;
    });
  };

  const handleAddSection = () => {
    setSections([
      ...sections,
      {
        id: Date.now(),
        type: 'text', // NEW: 'text' or 'quiz'
        visual: null, // This will store the selected visual object
        title: '',
        text: '',
        // Quiz-specific fields (only used when type === 'quiz')
        question: '',
        answers: [
          { id: `ans-${Date.now()}-0`, text: '' },
          { id: `ans-${Date.now()}-1`, text: '' }
        ],
        correctAnswerIndex: 0,
      },
    ]);
  };

  const handleSectionTypeChange = (idx, newType) => {
    setSections(prev => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        type: newType
      };
      return updated;
    });
  };

  const handleQuestionChange = (idx, value) => {
    setSections(prev => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        question: value
      };
      return updated;
    });
  };

  const handleAnswerChange = (idx, answerIdx, value) => {
    setSections(prev => {
      const updated = [...prev];
      const answers = [...updated[idx].answers];
      answers[answerIdx] = { ...answers[answerIdx], text: value };
      updated[idx] = {
        ...updated[idx],
        answers
      };
      return updated;
    });
  };

  const handleAddAnswer = (idx) => {
    setSections(prev => {
      const updated = [...prev];
      const answers = [...updated[idx].answers];
      answers.push({ id: `ans-${Date.now()}-${answers.length}`, text: '' });
      updated[idx] = {
        ...updated[idx],
        answers
      };
      return updated;
    });
  };

  const handleRemoveAnswer = (idx, answerIdx) => {
    setSections(prev => {
      const updated = [...prev];
      const answers = updated[idx].answers.filter((_, i) => i !== answerIdx);
      // Adjust correctAnswerIndex if necessary
      let newCorrectIdx = updated[idx].correctAnswerIndex;
      if (newCorrectIdx >= answers.length) {
        newCorrectIdx = Math.max(0, answers.length - 1);
      }
      if (newCorrectIdx === answerIdx && answers.length > 0) {
        newCorrectIdx = 0;
      } else if (newCorrectIdx > answerIdx) {
        newCorrectIdx--;
      }
      updated[idx] = {
        ...updated[idx],
        answers,
        correctAnswerIndex: newCorrectIdx
      };
      return updated;
    });
  };

  const handleCorrectAnswerChange = (idx, answerIdx) => {
    setSections(prev => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        correctAnswerIndex: answerIdx
      };
      return updated;
    });
  };

  const handleSelectVisual = (visual, idx) => {
    try {
    } catch (e) {}
    setSections(currentSections =>
      currentSections.map((section, i) =>
        i === idx ? { ...section, visual: visual } : section
      )
    );
  };

  const handleTitleChange = (idx, value) => {
    setSections(currentSections =>
      currentSections.map((section, i) =>
        i === idx ? { ...section, title: value } : section
      )
    );
  };

  const handleTextChange = (idx, value) => {
    setSections(currentSections =>
      currentSections.map((section, i) =>
        i === idx ? { ...section, text: value } : section
      )
    );
  };

  const handleMetadataChange = (field, value) => {
    setMetadata((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenPicker = (idx) => {
    setPickerSectionIndex(idx);
    setShowPicker(true);
    setModalView('options');
  };

  const handleClosePicker = () => {
    setShowPicker(false);
    setPickerSectionIndex(null);
    setModalView('options'); // Reset to options view
  };

  const handleVisualSelectedOrCreated = (visual) => {
    if (pickerSectionIndex !== null) {
      handleSelectVisual(visual, pickerSectionIndex);
    }
    handleClosePicker();
    handleVisualPickerModalClose();
  };

  const handleVisualPickerModalClose = () => {
    setShowVisualPickerModal(false);
    setVisualPickerDefaultView('my-visuals');
    setInitialSearchQuery('');
  };

  const handleOpenVisualPickerModal = () => {
    setShowPicker(false);
    setVisualPickerDefaultView('my-visuals');
    setInitialSearchQuery('');
    setShowVisualPickerModal(true);
  };

  const handleBackToMainPicker = () => {
    setShowVisualPickerModal(false);
    setShowPicker(true);
    setModalView('options');
    setVisualPickerDefaultView('my-visuals');
    setInitialSearchQuery('');
  };

  const handleAcceptSuggestion = (url, idx) => {
    handleAiFigureSelected(url, idx);
  };

  /** Pick a different extracted figure for this section — updates preview only; user still clicks Accept to finalize. */
  const handleSelectCandidateFigure = (figureUrl) => {
    if (pickerSectionIndex === null || !figureUrl) return;
    const i = pickerSectionIndex;
    setSections((prev) =>
      prev.map((section, j) =>
        j === i
          ? {
              ...section,
              visual: null,
              visual_id: undefined,
              visualUrl: figureUrl,
              isAccepted: false,
            }
          : section
      )
    );
    handleClosePicker();
  };

  const handleChooseAnotherSuggestion = (idx) => {
    setPickerSectionIndex(idx);
    setModalView("ai-suggestions");
    setShowPicker(true);
  };

  const handleOpenInteractiveMatchModal = (idx) => {
    setInteractiveMatchSectionIdx(idx);
    setShowInteractiveMatchModal(true);
  };

  const handleInteractiveMatchVisualChosen = (visual) => {
    if (interactiveMatchSectionIdx === null) return;
    const idx = interactiveMatchSectionIdx;
    setSections((currentSections) =>
      currentSections.map((section, i) =>
        i === idx
          ? {
              ...section,
              visual,
              visualUrl: visual.visualization_url,
              visual_id: visual.id,
              isAccepted: true,
            }
          : section
      )
    );
  };

  const handleAiFigureSelected = async (url, idx) => {
    const sectionIndex = idx !== undefined ? idx : pickerSectionIndex;
    if (sectionIndex === null) return;

    setPickerSectionIndex(sectionIndex); // Set this to identify which button is loading
    setIsCreatingAiVisual(true);
    setShowPicker(false); // Close modal if open

    // Promote candidate figure from staging to final assets bucket; do not create a library visual
    APIAssetPromote(
      url,
      'story_figure',
      { storyId: story?.id || null },
      (res) => {
        const finalUrl = res.finalUrl || res.url || url;
        // After promoting, create a lightweight web visual so we get a visual_id (backend expects ids on save)
        const title = sections[sectionIndex]?.title || `Figure ${sectionIndex + 1}`;
        const desc = sections[sectionIndex]?.visualSuggestion || 'Accepted figure';
        APIFileVisualization(
          finalUrl,
          title,
          desc,
          (newVisual) => {
            if (sectionIndex !== null) {
              setSections(currentSections =>
                currentSections.map((section, i) =>
                  i === sectionIndex
                    ? {
                        ...section,
                        visual: newVisual,
                        visualUrl: newVisual.visualization_url || finalUrl,
                        visual_id: newVisual.id,
                        isAccepted: true,
                      }
                    : section
                )
              );
            }
            handleClosePicker();
            handleVisualPickerModalClose();
            setIsCreatingAiVisual(false);
          },
          (err) => {
            // Fallback: keep URL-only visual so UI stays usable; save may still fail if backend strictly needs ids
            if (sectionIndex !== null) {
              setSections(currentSections =>
                currentSections.map((section, i) =>
                  i === sectionIndex
                    ? {
                        ...section,
                        visual: {
                          id: undefined,
                          title,
                          description: desc,
                          thumbnail: finalUrl,
                          visualization_url: finalUrl,
                        },
                        visualUrl: finalUrl,
                        isAccepted: true,
                      }
                    : section
                )
              );
            }
            handleClosePicker();
            handleVisualPickerModalClose();
            setIsCreatingAiVisual(false);
          },
          isOrg,
          { isSimple: true, isInPresentation: true, hideInLibrary: true }
        );
      },
      (error) => {
        const errorMessage = Array.isArray(error?.error) ? error.error[0] : (error?.message || "An unknown server error occurred.");
        // Allow user to proceed by embedding the staging URL so Save can still work
        if (sectionIndex !== null) {
          setSections(currentSections =>
            currentSections.map((section, i) =>
              i === sectionIndex
                ? {
                    ...section,
                    visual: {
                      id: undefined,
                      title: section.title || `Figure ${i + 1}`,
                      description: section.visualSuggestion || 'Accepted figure (staging)',
                      thumbnail: url,
                      visualization_url: url,
                    },
                    visualUrl: url,
                    isAccepted: true,
                  }
                : section
            )
          );
        }
        alert(`Failed to finalize figure (using staging): ${errorMessage}`);
        setIsCreatingAiVisual(false);
        setPickerSectionIndex(null);
      }
    );
  };

  const renderModalContent = () => {
    switch (modalView) {
      case "options":
        return (
          <Row className="text-center">
            {candidateFigures && candidateFigures.length > 0 && (
              <Col>
                <Button
                  variant="outline-primary"
                  className="p-4 w-100"
                  onClick={() => setModalView("ai-suggestions")}
                >
                  <Image
                    src={AiSparkleIcon}
                    alt="AI Suggestions"
                    style={{ height: "28px", marginBottom: "8px" }}
                  />
                  <div>Select from AI Suggestions</div>
                </Button>
              </Col>
            )}
            <Col>
              <Button
                variant="outline-primary"
                className="p-4 w-100"
                onClick={handleOpenVisualPickerModal}
              >
                <FontAwesomeIcon icon={faImages} size="2x" className="mb-2" />
                <div>Select from Your Visuals</div>
              </Button>
            </Col>
            <Col>
              <Button
                variant="outline-primary"
                className="p-4 w-100"
                onClick={() => setModalView("create")}
              >
                <FontAwesomeIcon icon={faPlus} size="2x" className="mb-2" />
                <div>Create a New Visual</div>
              </Button>
            </Col>
          </Row>
        );
      case "ai-suggestions":
        return (
          <div>
            <Button
              variant="link"
              onClick={() => setModalView("options")}
              className="mb-3 ps-0"
            >
              <FontAwesomeIcon icon={faArrowUp} rotation={270} className="me-2" />
              Back to options
            </Button>
            <h4>Select a Candidate Figure</h4>
            <p className="text-muted">
              These figures were extracted from your PDF. Click one to use it for this section&apos;s preview,
              then click <strong>Accept</strong> on the section card to finalize it in your story.
            </p>
            {isCreatingAiVisual ? (
              <div className="text-center p-5">
                <Spinner animation="border" role="status" />
                <p className="mt-3">Creating visual in your library...</p>
              </div>
            ) : (
              <Row className="g-3 candidate-figure-grid">
                {candidateFigures.map((figureUrl, cIdx) => (
                  <Col key={`${figureUrl}-${cIdx}`} xs={6} sm={4} md={3}>
                    <Card
                      className="h-100 cursor-pointer"
                      onClick={() => handleSelectCandidateFigure(figureUrl)}
                    >
                      <Card.Img
                        variant="top"
                        src={figureUrl}
                        alt={`Candidate figure ${cIdx + 1}`}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        );
      case "create":
        return (
          <CreateVisualFlow
            onSuccess={handleVisualSelectedOrCreated}
            onCancel={handleClosePicker}
            onlyOneVisual={true}
            lightTheme={true}
          />
        );
      default:
        return null;
    }
  };

  const getExportData = () => {
    // Prepares the data structure that the viewer app's App.jsx expects for its initialData prop
    return {
      metadata: metadata,
      sections: sections.map(section => {
        const baseSection = {
          type: section.type || 'text', // Default to 'text' for backward compatibility
          visualUrl: section.visual?.visualization_url || section.visualUrl || '',
          title: section.title,
        };
        
        if (section.type === 'quiz') {
          // Quiz-specific fields
          return {
            ...baseSection,
            question: section.question || '',
            answers: section.answers || [],
            correctAnswerIndex: section.correctAnswerIndex || 0,
          };
        } else {
          // Text section fields (default)
          return {
            ...baseSection,
            body: section.text, // The viewer's Scene component uses scene.body
          };
        }
      }),
    };
  };

  const handlePreviewInViewer = () => {
    if (saveState && saveState.state === 'saving') {
      alert('This story is still being saved. Please try again in a second.');
      return;
    }

    if (hasUnsavedChanges) {
      alert('Please save your story before previewing.');
      return;
    }

    if (!story?.url) {
      alert('Preview link is still being saved. Please try again in a second.');
      return;
    }

    window.open(story.url, '_blank', 'noopener');
  };



  const handleSave = () => {
    // --- VALIDATION START ---
    // For text sections, require a chosen library visual (or explicit acceptance after AI/staging flow).
    // PDF imports set staging visualUrl + suggestions but do not set isAccepted — those must not count as "done"
    // or save round-trips drop unpicked suggestions.
    const incompleteSection = sections.find(
      (section) =>
        section.type !== 'quiz' &&
        !section.visual &&
        !section.visual_id &&
        section.isAccepted !== true
    );
    if (incompleteSection) {
      alert(
        'Please accept each AI-suggested figure or choose a library visual for every text section before saving. (Quiz sections can optionally have no visual)'
      );
      return; // Stop the save process
    }
    // --- VALIDATION END ---

    if (saveState && saveState.state == 'saving') return;
    setSaveState({state: 'saving'});

    const data = getExportData();
    const firstSectionWithVisual = sections.find(s => s.visual && s.visual.thumbnail);
    const thumbnailUrl = firstSectionWithVisual ? firstSectionWithVisual.visual.thumbnail : null;

    const storyToSave = { metadata: shallowCopy(metadata), sections: [...sections], id: story.id };
    const translatedStory = {
      title: data.metadata.title || "Untitled Scrollytelling Story",
      description: data.metadata.abstract,
      thumbnail: thumbnailUrl,
      is_presentation: true,
      metadata: data.metadata,
      type: 'scrollytelling',
      slides: sections.map(s => {
        const hasLib = !!s.visual;
        const url = !hasLib && s.visualUrl ? s.visualUrl : undefined;
        const baseSlide = {
          type: s.type || 'text', // Include section type
          visual_id: hasLib ? s.visual.id : undefined,
          visual_url: url,                    // short form many endpoints accept
          visualization_url: url,             // also send long form for compatibility
          header: s.title,
        };
        
        if (s.type === 'quiz') {
          // Quiz-specific fields
          return {
            ...baseSlide,
            question: s.question || '',
            answers: s.answers || [],
            correctAnswerIndex: s.correctAnswerIndex || 0,
          };
        } else {
          // Text section fields
          return {
            ...baseSlide,
            intro: s.text,
          };
        }
      }),
      privacy: 'unlisted',
    };
    if (story.id) {
      translatedStory.id = story.id;
    }

    APIAddEditPresentation(
      translatedStory,
      (savedStory) => {
        const newStory = convertBackEndPresentationtoScrollytellingObject(savedStory, visCache);
        setStory(newStory);
        setSections(newStory.slides); // Update sections state
        setMetadata(newStory.metadata); // Update metadata state
        const currentState = { metadata: newStory.metadata, sections: newStory.slides };
        setLastSavedState(JSON.stringify(currentState));
        setHasUnsavedChanges(false);
        setSaveState({state: 'saved'});

        // Ensure each slide's visual is in the cache so "Edit" from the library
        // can resolve visuals without a full API refetch
        setVisCache(prevCache => {
          const updated = { ...prevCache };
          sections.forEach(s => {
            if (s.visual && s.visual.id) {
              updated[s.visual.id] = s.visual;
            }
          });
          return updated;
        });
        
        // If this came from content dashboard, add to channel
        if (addToChannelOnSave && channelEmail && userInfo) {
          const channelConfig = loadChannelConfig(channelEmail);
          if (channelConfig) {
            addVisualToChannel(channelEmail, savedStory.id, channelConfig);
            console.log('Added scrollytelling story to channel:', savedStory.id);
          }
        }
      },
      (err) => {
        setSaveState({state: 'error', msg: err.message || err.toString()});
      },
      isOrg,
      'scrollytelling'
    );
  };

  return (
    <>
      <Modal show={showMobileWarning} onHide={() => setShowMobileWarning(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Desktop Recommended</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This feature is best used on Desktop. Mobile support is limited.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowMobileWarning(false)}>
            Understood
          </Button>
        </Modal.Footer>
      </Modal>

      <Container fluid className="scrollytelling-builder p-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h1 className="display-4 mb-2">Scrollytelling Builder</h1>
            <p className="text-muted lead">Create an interactive, scroll-based narrative experience</p>
            <div style={{ marginTop: '8px' }}>
              <ReportInaccuraciesButton context="Scrollytelling Builder" variant="text" />
            </div>
          </Col>
          <Col xs="auto" className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              onClick={() => setShowAccessCodeModal(true)}
              className="me-2"
              style={{ minWidth: '180px' }} // Give the button a fixed width
            >
                <>
                  <Image src={AiSparkleIcon} alt="AI Assistant" style={{ height: '30px', marginRight: '8px' }} />
                  AI Draft from PDF
                </>
            </Button>
            <Button
              variant="success"
              onClick={handleSave}
              disabled={saveState && saveState.state == 'saving' || !hasUnsavedChanges}
              className="me-2"
            >
              {saveState && saveState.state == 'saving' ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">Saving...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  Save
                </>
              )}
            </Button>
            <Button
              variant="outline-info"
              onClick={handlePreviewInViewer}
              className="me-2"
              disabled={saveState && saveState.state === 'saving'}
            >
              Preview in Viewer
            </Button>
            <OverlayTrigger
              placement="top"
              delay={{ show: 1500, hide: 250 }}
              overlay={<Tooltip id="tooltip-share-story">Share</Tooltip>}
            >
              <Button variant="outline-secondary" onClick={() => setShowShareModal(true)} disabled={!story.id}>
                <FontAwesomeIcon icon={faPaperPlane} />
              </Button>
            </OverlayTrigger>
            {saveState && saveState.state == 'error' && (
              <div className="text-danger">
                {saveState.msg}
              </div>
            )}
            {saveState && saveState.state == 'saved' && hasUnsavedChanges && (
              <div className="text-success">
                Saved
              </div>
            )}
          </Col>
        </Row>


        <Row>
          {/* Main Editor Area - Left Column */}
          <Col md={7} lg={8}>
            <Card className="mb-4 editor-card">
              <Card.Body>
                {sections.length === 0 ? (
                  <div className="text-center p-5 empty-state">
                    <h3 className="mb-3">Start Building Your Story</h3>
                    <p className="text-muted mb-4">Add your first section to begin creating your scrollytelling experience</p>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleAddSection}
                      className="px-4"
                    >
                      Add First Section
                    </Button>
                  </div>
                ) : (
                  sections.map((section, idx) => (
                    <Card key={section.id} className="mb-3 section-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h4 className="mb-0">Section {idx + 1}</h4>
                          <div>
                            <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleMoveSectionUp(idx)} disabled={idx === 0}>
                              <FontAwesomeIcon icon={faArrowUp} />
                            </Button>
                            <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleMoveSectionDown(idx)} disabled={idx === sections.length - 1}>
                              <FontAwesomeIcon icon={faArrowDown} />
                            </Button>
                            <Button variant="outline-danger" size="sm" className="me-2" onClick={() => setSections(secs => secs.filter(s => s.id !== section.id))}>Delete</Button>
                          </div>
                        </div>
                        
                        {/* Section Type Selector */}
                        <Form.Group className="mb-3">
                          <Form.Label><strong>Section Type</strong></Form.Label>
                          <Form.Select
                            value={section.type || 'text'}
                            onChange={e => handleSectionTypeChange(idx, e.target.value)}
                          >
                            <option value="text">Text Section</option>
                            <option value="quiz">Quiz Section</option>
                          </Form.Select>
                        </Form.Group>
                        
                        <div className="mb-3">{/* Visual selector - make optional for quiz */}
                          {(section.type === 'quiz') && (
                            <div className="mb-2 text-muted small">
                              Visual is optional for quiz sections
                            </div>
                          )}
                          {section.visual ? (
                            <>
                              <Button
                                variant={"outline-primary"}
                                onClick={(e) => {
                                  handleOpenPicker(idx);
                                  e.currentTarget.blur();
                                }}
                              >
                                {"Change Visual"}
                              </Button>
                              <div className="mt-3 d-flex align-items-center">
                                <Image src={section.visual.thumbnail || 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg'} alt={section.visual.title} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, marginRight: 16 }} />
                                <div>
                                  <div className="fw-bold">{section.visual.title}</div>
                                  <div className="text-muted" style={{ fontSize: '0.95em' }}>{section.visual.description || 'No description'}</div>
                                </div>
                              </div>
                            </>
                          ) : section.visualUrl ? (
                            <div className="mt-3 p-3 bg-light border rounded">
                              <div className="d-flex align-items-center">
                                <Image
                                  src={section.visualUrl}
                                  alt="AI-suggested visual"
                                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, marginRight: 16 }}
                                  onLoad={() => {
                                  }}
                                  onError={(e) => {
                                    try {
                                      e.currentTarget.src = 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg';
                                    } catch (err) {}
                                  }}
                                />
                                <div>
                                  <strong>AI Suggestion:</strong>
                                  <p className="mb-0 fst-italic" dir={getTextDirection(section.visualSuggestion)}>{section.visualSuggestion || 'Here is a suggested visual for this section.'}</p>
                                </div>
                              </div>
                              <div className="mt-3 d-flex justify-content-start">
                                <Button variant="success" size="sm" className="me-2" onClick={() => handleAcceptSuggestion(section.visualUrl, idx)} disabled={isCreatingAiVisual && pickerSectionIndex === idx}>
                                  {isCreatingAiVisual && pickerSectionIndex === idx ? (
                                    <>
                                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                      <span className="ms-2">Accepting...</span>
                                    </>
                                  ) : (
                                    <>
                                      <FontAwesomeIcon icon={faCheck} className="me-2" />
                                      Accept
                                    </>
                                  )}
                                </Button>
                                <DropdownButton
                                  as={ButtonGroup}
                                  size="sm"
                                  variant="outline-secondary"
                                  title="Choose Other..."
                                  className="me-2"
                                  disabled={isCreatingAiVisual  && pickerSectionIndex === idx}
                                >
                                  <Dropdown.Item onClick={() => handleChooseAnotherSuggestion(idx)}>
                                    <FontAwesomeIcon icon={faShuffle} className="me-2" />
                                    Other Extracted Figures
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleOpenPicker(idx)}>
                                    <FontAwesomeIcon icon={faImages} className="me-2" />
                                    Choose/Create Other
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleOpenInteractiveMatchModal(idx)}>
                                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                                    Find Matching Interactive
                                  </Dropdown.Item>
                                </DropdownButton>
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant={'primary'}
                              onClick={(e) => {
                                handleOpenPicker(idx);
                                e.currentTarget.blur();
                              }}
                            >
                              {'Select Visual'}
                            </Button>
                          )}
                        </div>
                        <Form.Group className="mb-3">
                          <Form.Label>Section Title</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter section title"
                            value={section.title}
                            onChange={e => handleTitleChange(idx, e.target.value)}
                            dir={getTextDirection(section.title)}
                          />
                        </Form.Group>
                        
                        {/* Conditional rendering based on section type */}
                        {section.type === 'quiz' ? (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>Quiz Question</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Enter your quiz question"
                                value={section.question || ''}
                                onChange={e => handleQuestionChange(idx, e.target.value)}
                                dir={getTextDirection(section.question)}
                              />
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                              <Form.Label>Answer Options</Form.Label>
                              {(section.answers || []).map((answer, answerIdx) => (
                                <div key={answer.id} className="d-flex align-items-start mb-2">
                                  <Form.Check
                                    type="radio"
                                    name={`correct-answer-${section.id}`}
                                    checked={section.correctAnswerIndex === answerIdx}
                                    onChange={() => handleCorrectAnswerChange(idx, answerIdx)}
                                    className="me-2 mt-2"
                                    label=""
                                    title="Mark as correct answer"
                                  />
                                  <Form.Control
                                    type="text"
                                    placeholder={`Answer ${answerIdx + 1}`}
                                    value={answer.text}
                                    onChange={e => handleAnswerChange(idx, answerIdx, e.target.value)}
                                    className="me-2"
                                  />
                                  {section.answers.length > 2 && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleRemoveAnswer(idx, answerIdx)}
                                    >
                                      ×
                                    </Button>
                                  )}
                                </div>
                              ))}
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleAddAnswer(idx)}
                                className="mt-2"
                              >
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Add Answer
                              </Button>
                              <div className="text-muted small mt-2">
                                Use the radio button to mark the correct answer
                              </div>
                            </Form.Group>
                          </>
                        ) : (
                          <Form.Group className="mb-3">
                            <Form.Label>Overlay Text (Body)</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              placeholder="Enter overlay text for this section (this will be the 'body' for the viewer)"
                              value={section.text}
                              onChange={e => handleTextChange(idx, e.target.value)}
                              dir={getTextDirection(section.text)}
                            />
                          </Form.Group>
                        )}
                      </Card.Body>
                    </Card>
                  ))
                )}
                {sections.length > 0 && (
                  <Button variant="secondary" onClick={handleAddSection} className="mt-3 w-100">
                    Add Another Section
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Metadata/Intro Section - Right Column */}
          <Col md={5} lg={4}>
            <Card className="p-4 sticky-top" style={{ top: '20px', zIndex: 0 /*Allow drop down menus to be on top of this card*/ }}>
              <h4 className="mb-3">Story Details</h4>
              <Form.Group className="mb-3">
                <Form.Label><strong>Story Title</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter story title"
                  value={metadata.title}
                  onChange={e => handleMetadataChange('title', e.target.value)}
                  dir={getTextDirection(metadata.title)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Author Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter author name"
                  value={metadata.author}
                  onChange={e => handleMetadataChange('author', e.target.value)}
                  dir={getTextDirection(metadata.author)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Author URL</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter author profile or website URL"
                  value={metadata.authorUrl}
                  onChange={e => handleMetadataChange('authorUrl', e.target.value)}
                  dir={getTextDirection(metadata.authorUrl)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Abstract / Overview</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter a brief abstract or overview for the story"
                  value={metadata.abstract}
                  onChange={e => handleMetadataChange('abstract', e.target.value)}
                  dir={getTextDirection(metadata.abstract)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={metadata.date}
                  onChange={e => handleMetadataChange('date', e.target.value)}
                />
              </Form.Group>
            </Card>
          </Col>
        </Row>

        <Modal show={showPicker} onHide={handleClosePicker} size="lg" style={{ display: showVisualPickerModal ? 'none' : '' }}>
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>
              {modalView === 'options' && 'Add Visual to Section'}
              {modalView === 'create' && 'Create New Visual'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {renderModalContent()}
          </Modal.Body>
        </Modal>

        {showVisualPickerModal && (
          <VisualPickerModal
            show={showVisualPickerModal}
            onHide={handleVisualPickerModalClose}
            onSelect={handleVisualSelectedOrCreated}
            visCache={visCache}
            userInfo={userInfo}
            filterVisualIdsList={sections.filter(s => s.visual).map(s => s.visual.id)}
            owner={isOrg? "team" : "self"}
            defaultView={visualPickerDefaultView}
            initialSearchQuery={initialSearchQuery}
            onBackToOptions={handleBackToMainPicker}
          />
        )}
      </Container>

      {showShareModal && (
        <ShareModal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          url={story.url}
          qr={story.qr_url}
          id={story.id}
          isPresentation={true}
        />
      )}

      <AiDraftModal
        show={showAiDraftModal}
        onHide={() => setShowAiDraftModal(false)}
        onGenerate={handlePdfUpload}
        generationStatus={generationStatus}
      />

      <AccessCodeModal
        show={showAccessCodeModal}
        onHide={() => setShowAccessCodeModal(false)}
        onAccessGranted={() => {
          setShowAccessCodeModal(false);
          setShowAiDraftModal(true);
        }}
        featureName="AI Draft from PDF"
        featureKey="create_scrollytelling_from_pdf"
      />

      {showInteractiveMatchModal && interactiveMatchSectionIdx !== null && sections[interactiveMatchSectionIdx] && (
        <ScrollytellingInteractiveMatchModal
          show={showInteractiveMatchModal}
          onHide={() => {
            setShowInteractiveMatchModal(false);
            setInteractiveMatchSectionIdx(null);
          }}
          section={sections[interactiveMatchSectionIdx]}
          userInfo={userInfo}
          setVisCache={setVisCache}
          onVisualChosen={handleInteractiveMatchVisualChosen}
          isOrg={isOrg}
        />
      )}
    </>
  );
};

export default ScrollytellingBuilderPage;